import { Injectable } from "@angular/core";
import { AbstractControl, ValidatorFn } from "@angular/forms";
import { LoadingService } from "./loading.service";
import { Router } from "@angular/router";
import { AlertService } from "./alert.service";
import { ToastrService } from "ngx-toastr";
import { ProjectConfigService } from "./project-config.service";
import { Platform } from "@angular/cdk/platform";

@Injectable({
  providedIn: 'root'
})
export class XtrasService {

  constructor(
    private alert: AlertService,
    private load: LoadingService,
    private toast: ToastrService,
    private router: Router,
    private projectConfig: ProjectConfigService,
    public platform: Platform,
  ) {}

  filterEmptyParams(formData: { [key: string]: any }) {
    return Object.fromEntries(
      Object.entries(formData).filter(
        ([_, value]) => value !== null && value !== undefined && value !== ''
      )
    );
  }

  lengthItems(items: any) {
    if(Array.isArray(items)) {
      if(items.length >= 1) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  scrollToTarget(el: HTMLElement) { // example <div #algo> scrollToTarget(algo)
    el.scrollIntoView({behavior: 'smooth'});
  }

  checkFieldsSame(field1: string, field2: string): ValidatorFn {
    return (group: AbstractControl): { [key: string]: any } | null => {
      const _field1 = group.get(field1);
      const _field2 = group.get(field2);
      return _field1 && _field2 && _field1.value === _field2.value ? null : { notSame: true };
    };
  }

  getErrorApi(result: any, loading:boolean = false, redirectError: string = '/', errorDefault?:string) {
    if(loading) {
      this.load.dismiss();
    }
    if(result?.others?.redirect_url) {
      this.router.navigateByUrl(result?.others?.redirect_url);
      this.alertErrorApi(result, errorDefault);
      return;
    }
    if(result['redirect']) {
      this.router.navigateByUrl(redirectError);
    }
    this.alertErrorApi(result, errorDefault);
  }

  alertErrorApi(result: any, errorDefault?: string) {
    if(result['message']) {
      if(result['errors']) {
        this.alert.alertCapYei({
          type: 'error',
          message: result['message'],
          buttons: {
            cancel: { show: false },
            confirm: { show: true, text: 'Entendido' },
          },
          description: result['errors']
        }).then(() => {
          this.reloadPage(result?.others?.reload ?? false);
        });
      } else {
        this.alert.alertCapYei({
          type: 'error',
          message: 'Error',
          buttons: {
            cancel: { show: false },
            confirm: { show: true, text: 'Entendido' },
          },
          description: result['message']
        }).then(() => {
          this.reloadPage(result?.others?.reload ?? false);
        });
      }
    } else {
      if(result['errors']) {
        this.alert.alertCapYei({
          type: 'error',
          message: '¡Error!',
          buttons: {
            cancel: { show: false },
            confirm: { show: true, text: 'Entendido' },
          },
          description: result['errors']
        }).then(() => {
          this.reloadPage(result?.others?.reload ?? false);
        });
      } else {
        this.toast.error((errorDefault ?? 'No se pudo obtener la información, intente nuevamente.'), '¡Error!',  {timeOut: 5000, closeButton: true, progressBar: true});
        if(result?.others?.reload) {
          setTimeout(() => {
            this.reloadPage(true);
          }, 3000);
        }
      }
    }
  }

  async reloadPage(reload: boolean = false) {
    if(reload) {
      const currentUrl = this.router.url;
      await this.router.navigate(['/'], { skipLocationChange: true, replaceUrl: true });
      await this.router.navigate([currentUrl], { skipLocationChange: true, replaceUrl: true });
    }
  }

  determineColorType(color: string): 'light' | 'dark' | 'normal' | 'unknown' {
    try {
      // Convertimos el color a formato RGBA
      const rgba = this.parseColorToRGBA(color);
      if (!rgba) return 'unknown';

      // Calculamos el brillo usando la fórmula de luminosidad perceptiva
      const [r, g, b] = rgba;
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;

      // Definimos umbrales más estrictos
      const LIGHT_THRESHOLD = 200;  // Más cerca del blanco (255)
      const DARK_THRESHOLD = 60;    // Más cerca del negro (0)

      if (brightness >= LIGHT_THRESHOLD) {
        return 'light';
      } else if (brightness <= DARK_THRESHOLD) {
        return 'dark';
      } else {
        return 'normal';  // Colores en el rango medio
      }
    } catch {
      return 'unknown';
    }
  }

  // Convierte cualquier formato de color a RGBA
  parseColorToRGBA(color: string): [number, number, number, number] | null {
    // Eliminar espacios y convertir a minúsculas
    const normalized = color.trim().toLowerCase();

    // 1. Hex color (#rgb, #rgba, #rrggbb, #rrggbbaa)
    if (/^#([a-f0-9]{3,4}|[a-f0-9]{6}|[a-f0-9]{8})$/.test(normalized)) {
      let hex = normalized.substring(1);

      // Expandir formato corto (#rgb → #rrggbb)
      if (hex.length === 3 || hex.length === 4) {
        hex = hex.split('').map(c => c + c).join('');
      }

      // Parsear componentes
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const a = hex.length === 8 ? parseInt(hex.substring(6, 8), 16) / 255 : 1;

      return [r, g, b, a];
    }

    // 2. RGB/RGBA color (rgb(255, 255, 255) o rgba(255, 255, 255, 0.5))
    const rgbMatch = normalized.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1]);
      const g = parseInt(rgbMatch[2]);
      const b = parseInt(rgbMatch[3]);
      const a = rgbMatch[4] ? parseFloat(rgbMatch[4]) : 1;
      return [r, g, b, a];
    }

    // 3. Nombres de colores básicos (opcional)
    const colorNames: Record<string, [number, number, number, number]> = {
      'white': [255, 255, 255, 1],
      'black': [0, 0, 0, 1],
      'red': [255, 0, 0, 1],
      'green': [0, 128, 0, 1],
      'blue': [0, 0, 255, 1],
      // Añadir más colores según necesidad
    };

    if (colorNames[normalized]) {
      return colorNames[normalized];
    }

    return null;
  }

  loadInlineScriptHead(text: string, noscript: boolean = false) {
    const script:any = document.createElement('script');
    if(!noscript) {
      script.type = 'text/javascript';
      script.text = text;
      document.head.appendChild(script);
    } else {
      const noscript = document.createElement('noscript');
      noscript.innerHTML = text;
      document.head.appendChild(noscript);
    }
  }

  loadInlineScriptBody(text: string, noscript: boolean = false) {
    const script:any = document.createElement('script');
    if(!noscript) {
      script.type = 'text/javascript';
      script.text = text;
      document.body.appendChild(script);
    } else {
      const noscript = document.createElement('noscript');
      noscript.innerHTML = text;
      document.body.appendChild(noscript);
    }
  }

  loadScriptBody(src: string, async: boolean = false, param?: { param: string, value: string }) {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = src;
    script.async = async;
    if(param) {
      (script as any)[param.param] = param.value;
    }
    document.body.appendChild(script);
  }

  staticMap(lat: string|number, lng: string|number) {
    return `https://maps.googleapis.com/maps/api/staticmap?key=${this.projectConfig.getConfig().keyMap}&size=500x500&zoom=17&maptype=roadmap&markers=color:red|${lat},${lng}`;
  }

  isAppIos() {
    return this.platform.IOS;
  }

  isAppAndroid() {
    return this.platform.ANDROID;
  }

  isBrowser() {
    return this.platform.isBrowser;
  }
}
